import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  FiArrowLeft, 
  FiUsers, 
  FiX, 
  FiImage, 
  FiVideo,
  FiCheckCircle,
  FiClock,
  FiFileText,
  FiPlus,
  FiAward, 
  FiBriefcase,
  FiEdit2,
  FiGift,
  FiUser,
  FiCreditCard,
  FiCamera,
  FiPhone,
  FiThumbsUp,
  FiThumbsDown,
  FiShare2,
  FiMail
} from 'react-icons/fi';
import { FaRupeeSign } from 'react-icons/fa';
import { database, ref as dbRef, get, set, remove, update } from '../firebase'; // for database
import { storage, storageRef, getDownloadURL, uploadBytes } from '../firebase'; // for storage
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// Constants for Firebase paths
const FIREBASE_PATHS = {
  USERS: 'videoplayer/Users',
  PROFILE_IMAGE: 'videoplayer/UserProfile',
  PHOTO_UPLOAD: 'videoplayer/photoUpload',
  VIDEO_UPLOAD: 'videoplayer/videoUpload',
  WITHDRAW_REQUESTS: 'videoplayer/withdraw',
  TASKS: 'videoplayer/tasks',
  GIFTS: 'videoplayer/Users', // Base path for gifts (will append /<sanitizedMobile>/gifts)
};

// Modal component for viewing images or videos
const MediaModal = ({ mediaUrl, mediaType, onClose }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/90 flex items-center justify-center z-50"
    >
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.9 }}
        className="relative bg-white rounded-2xl shadow-2xl p-4 max-w-4xl w-full mx-4"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-600 hover:text-gray-900 bg-white/80 rounded-full p-1 z-10"
          aria-label="Close modal"
        >
          <FiX className="text-xl" />
        </button>
        {mediaType === 'image' ? (
          <motion.img
            src={mediaUrl}
            alt="Full-size media"
            className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          />
        ) : (
          <motion.video
            src={mediaUrl}
            controls
            className="w-full h-auto max-h-[80vh] rounded-lg"
            autoPlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          />
        )}
      </motion.div>
    </motion.div>
  );
};

// Task Form Modal component (used for both creating and editing tasks)
const TaskFormModal = ({ taskForm, setTaskForm, handleTaskSubmit, closeTaskForm, isEditing = false, taskPhoto, setTaskPhoto }) => {
  const handleTaskInputChange = (e) => {
    const { name, value } = e.target;
    setTaskForm(prev => ({ ...prev, [name]: value }));
  };

  const handlePhotoChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setTaskPhoto(e.target.files[0]);
    } else {
      setTaskPhoto(null);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/90 flex items-center justify-center z-50"
    >
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.9 }}
        className="relative bg-white rounded-2xl shadow-2xl p-6 max-w-lg w-full mx-4"
      >
        <button
          onClick={closeTaskForm}
          className="absolute top-4 right-4 text-gray-600 hover:text-gray-900 bg-white/80 rounded-full p-1 z-10"
          aria-label="Close task form"
        >
          <FiX className="text-xl" />
        </button>
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
          {isEditing ? (
            <>
              <FiEdit2 className="mr-2 text-indigo-500" />
              Edit Task {taskForm.taskId}
            </>
          ) : (
            <>
              <FiPlus className="mr-2 text-indigo-500" />
              Assign New Task
            </>
          )}
          {taskForm.withdrawRequestId && (
            <span className="ml-2 text-sm text-gray-600">
              (Related to Withdrawal Request ID: {taskForm.withdrawRequestId})
            </span>
          )}
        </h3>
        <form onSubmit={handleTaskSubmit}>
          <div className="mb-4">
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
              Task Title
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={taskForm.title}
              onChange={handleTaskInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Enter task title"
              required
            />
          </div>
          <div className="mb-4">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Task Description
            </label>
            <textarea
              id="description"
              name="description"
              value={taskForm.description}
              onChange={handleTaskInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              rows="3"
              placeholder="Enter task description"
              required
            ></textarea>
          </div>
          <div className="mb-4">
            <label htmlFor="taskPhoto" className="block text-sm font-medium text-gray-700 mb-1">
              Task Photo (optional)
            </label>
            <input
              type="file"
              id="taskPhoto"
              name="taskPhoto"
              accept="image/*"
              onChange={handlePhotoChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
            {taskPhoto && (
              <>
                <div className="mt-2 text-xs text-gray-600">Selected: {taskPhoto.name}</div>
                <div className="mt-2">
                  <img
                    src={URL.createObjectURL(taskPhoto)}
                    alt="Task Preview"
                    className="h-32 w-32 object-cover rounded border"
                  />
                </div>
              </>
            )}
          </div>
          <div className="flex space-x-4">
            <button
              type="submit"
              className="flex-1 bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center"
            >
              {isEditing ? (
                <>
                  <FiEdit2 className="mr-2" />
                  Update Task
                </>
              ) : (
                <>
                  <FiPlus className="mr-2" />
                  Assign Task
                </>
              )}
            </button>
            <button
              type="button"
              onClick={closeTaskForm}
              className="flex-1 bg-gray-300 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors flex items-center justify-center"
            >
              <FiX className="mr-2" />
              Cancel
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Gift Form Modal component (used for both creating and editing gifts)
const GiftFormModal = ({ giftForm, setGiftForm, handleGiftSubmit, closeGiftForm, isEditing = false }) => {
  const handleGiftInputChange = (e) => {
    const { name, value } = e.target;
    setGiftForm(prev => ({ ...prev, [name]: value }));
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/90 flex items-center justify-center z-50"
    >
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.9 }}
        className="relative bg-white rounded-2xl shadow-2xl p-6 max-w-lg w-full mx-4"
      >
        <button
          onClick={closeGiftForm}
          className="absolute top-4 right-4 text-gray-600 hover:text-gray-900 bg-white/80 rounded-full p-1 z-10"
          aria-label="Close gift form"
        >
          <FiX className="text-xl" />
        </button>
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
          {isEditing ? (
            <>
              <FiEdit2 className="mr-2 text-purple-500" />
              Edit Gift {giftForm.giftId}
            </>
          ) : (
            <>
              <FiGift className="mr-2 text-purple-500" />
              Assign Extra Commission
            </>
          )}
        </h3>
        <form onSubmit={handleGiftSubmit}>
          <div className="mb-4">
            <label htmlFor="giftName" className="block text-sm font-medium text-gray-700 mb-1">
              Extra Commission Name
            </label>
            <input
              type="text"
              id="giftName"
              name="giftName"
              value={giftForm.giftName}
              onChange={handleGiftInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              placeholder="Enter Extra Commission"
              required
            />
          </div>
          <div className="flex space-x-4">
            <button
              type="submit"
              className="flex-1 bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center"
            >
              {isEditing ? (
                <>
                  <FiEdit2 className="mr-2" />
                  Update Extra Commission
                </>
              ) : (
                <>
                  <FiGift className="mr-2" />
                  Assign Extra Commission
                </>
              )}
            </button>
            <button
              type="button"
              onClick={closeGiftForm}
              className="flex-1 bg-gray-300 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors flex items-center justify-center"
            >
              <FiX className="mr-2" />
              Cancel
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

const UserProfile = () => {
  const { userId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [videoAds, setVideoAds] = useState({}); // { videoId: [ads] }
  const [withdrawRequests, setWithdrawRequests] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [gifts, setGifts] = useState([]);
  const [taskForm, setTaskForm] = useState({ taskId: '', title: '', description: '', withdrawRequestId: '' });
  const [giftForm, setGiftForm] = useState({ giftId: '', giftName: '', points: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('profile');
  const [mediaModal, setMediaModal] = useState({ isOpen: false, mediaUrl: null, mediaType: null });
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [showGiftForm, setShowGiftForm] = useState(false);
  const [isEditingTask, setIsEditingTask] = useState(false);
  const [isEditingGift, setIsEditingGift] = useState(false);
  const [newRequestIds, setNewRequestIds] = useState([]);
  const sliderRef = useRef(null);
  const [taskPhoto, setTaskPhoto] = useState(null);
  const [showGivePointsModal, setShowGivePointsModal] = useState(false);
  const [givePointsTaskId, setGivePointsTaskId] = useState(null);
  const [givePointsValue, setGivePointsValue] = useState('');
  const [givePointsLoading, setGivePointsLoading] = useState(false);
  // Add state for deposits modal and data
  const [showDepositsModal, setShowDepositsModal] = useState(false);
  const [deposits, setDeposits] = useState([]);
  const [depositsLoading, setDepositsLoading] = useState(false);
  const [depositsError, setDepositsError] = useState('');
  const [depositImageModal, setDepositImageModal] = useState({ open: false, url: null });
  // Add state for invite modal and data
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteBatches, setInviteBatches] = useState([]);
  const [selectedInviteBatchIdx, setSelectedInviteBatchIdx] = useState(0);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [invitePoints, setInvitePoints] = useState({}); // { [inviteKey]: points }
  const [invitePointsLoading, setInvitePointsLoading] = useState({});
  const [batchPoints, setBatchPoints] = useState('');
  const [batchPointsLoading, setBatchPointsLoading] = useState(false);
  // Add state for photo and video engagement data
  const [photoEngagement, setPhotoEngagement] = useState({}); // { photoId: { likes: count, shares: count } }
  const [videoEngagement, setVideoEngagement] = useState({}); // { videoId: { likes: count, dislikes: count, shares: count } }
  const [subscriberCount, setSubscriberCount] = useState(0); // Total subscriber count for the user

  // Function to fetch subscriber count
  const fetchSubscriberCount = async (sanitizedMobile) => {
    try {
      const subscriptionsRef = dbRef(database, `videoplayer/subscriptions/${sanitizedMobile}`);
      const subscriptionsSnapshot = await get(subscriptionsRef);
      if (subscriptionsSnapshot.exists()) {
        const subscribers = subscriptionsSnapshot.val();
        const count = Object.keys(subscribers).length;
        setSubscriberCount(count);
      } else {
        setSubscriberCount(0);
      }
    } catch (err) {
      console.error('Error fetching subscriber count:', err);
      setSubscriberCount(0);
    }
  };

  // Function to fetch engagement data for photos and videos
  const fetchEngagementData = async (photosData, videosData, sanitizedMobile) => {
    try {
      const photoEngagementData = {};
      const videoEngagementData = {};

      // Fetch engagement data for photos
      await Promise.all(photosData.map(async (photo) => {
        if (!photo.id) return;
        
        try {
          // Use dbKey if available, otherwise use id
          const photoKey = photo.dbKey || photo.id;
          
          const [likesSnapshot, sharesSnapshot] = await Promise.all([
            get(dbRef(database, `videoplayer/photoUpload/${sanitizedMobile}/${photoKey}/likes`)),
            get(dbRef(database, `videoplayer/photoUpload/${sanitizedMobile}/${photoKey}/shares`))
          ]);

          photoEngagementData[photo.id] = {
            likes: likesSnapshot.exists() ? Object.keys(likesSnapshot.val()).length : 0,
            shares: sharesSnapshot.exists() ? Object.keys(sharesSnapshot.val()).length : 0
          };
        } catch (photoErr) {
          console.error(`Error fetching engagement for photo ${photo.id}:`, photoErr);
          photoEngagementData[photo.id] = { likes: 0, shares: 0 };
        }
      }));

      // Fetch engagement data for videos
      await Promise.all(videosData.map(async (video) => {
        if (!video.id) return;
        
        try {
          // Use dbKey if available, otherwise use id
          const videoKey = video.dbKey || video.id;
          
          const [likesSnapshot, dislikesSnapshot, sharesSnapshot] = await Promise.all([
            get(dbRef(database, `videoplayer/videoUpload/${sanitizedMobile}/${videoKey}/likes`)),
            get(dbRef(database, `videoplayer/videoUpload/${sanitizedMobile}/${videoKey}/dislikes`)),
            get(dbRef(database, `videoplayer/videoUpload/${sanitizedMobile}/${videoKey}/share`))
          ]);

          videoEngagementData[video.id] = {
            likes: likesSnapshot.exists() ? Object.keys(likesSnapshot.val()).length : 0,
            dislikes: dislikesSnapshot.exists() ? Object.keys(dislikesSnapshot.val()).length : 0,
            shares: sharesSnapshot.exists() ? Object.keys(sharesSnapshot.val()).length : 0
          };
        } catch (videoErr) {
          console.error(`Error fetching engagement for video ${video.id}:`, videoErr);
          videoEngagementData[video.id] = { likes: 0, dislikes: 0, shares: 0 };
        }
      }));

      setPhotoEngagement(photoEngagementData);
      setVideoEngagement(videoEngagementData);
    } catch (err) {
      console.error('Error fetching engagement data:', err);
    }
  };

  // Fetch user data
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        
        // Sanitize userId for Firebase path
        const sanitizedMobile = userId.replace(/[^0-9]/g, '_');

        // Fetch user data and related information
        const userRef = dbRef(database, `${FIREBASE_PATHS.USERS}/${sanitizedMobile}`);
        const [userSnapshot, photosSnapshot, videosSnapshot, withdrawSnapshot, tasksSnapshot, giftsSnapshot] = 
          await Promise.all([
            get(userRef),
            get(dbRef(database, `${FIREBASE_PATHS.PHOTO_UPLOAD}/${sanitizedMobile}`)),
            get(dbRef(database, `${FIREBASE_PATHS.VIDEO_UPLOAD}/${sanitizedMobile}`)),
            get(dbRef(database, `${FIREBASE_PATHS.WITHDRAW_REQUESTS}/${sanitizedMobile}`)),
            get(dbRef(database, `${FIREBASE_PATHS.TASKS}/${sanitizedMobile}`)),
            get(dbRef(database, `${FIREBASE_PATHS.GIFTS}/${sanitizedMobile}/gifts`))
          ]);

        // Process user data
        const dbUserData = userSnapshot.exists() ? userSnapshot.val() : {};
        let profilePictureUrl = dbUserData.profilePictureUrl || null;

        setUser({
          name: dbUserData.name || 'N/A',
          surname: dbUserData.surname || 'N/A',
          email: dbUserData.email || 'N/A',
          business: dbUserData.business || 'N/A',
          plan: dbUserData.plan || 'N/A',
          gst: dbUserData.gst ? `${dbUserData.gst}%` : 'N/A',
          mobile: userId,
          points: dbUserData.points || 0,
          profilePictureUrl,
          payment: dbUserData.payment || 'N/A',
          screenshotUrl: dbUserData.screenshotUrl || null,
          status: dbUserData.status || 'N/A',
          timestamp: dbUserData.timestamp || 'N/A',
          visitBranch: dbUserData.visitBranch || 'N/A',
        });

        // Process media data
        const photosData = photosSnapshot.exists() 
          ? Object.entries(photosSnapshot.val()).map(([key, photo]) => ({ ...photo, id: key }))
          : [];
        const videosData = videosSnapshot.exists() 
          ? Object.entries(videosSnapshot.val()).map(([key, video]) => ({ ...video, id: key }))
          : [];
        
        setPhotos(photosData);
        setVideos(videosData);

        // Fetch engagement data for photos and videos
        await fetchEngagementData(photosData, videosData, sanitizedMobile);

        // Fetch subscriber count
        await fetchSubscriberCount(sanitizedMobile);

        // Process withdrawal requests and sort by ID
        const newRequests = withdrawSnapshot.exists()
          ? Object.entries(withdrawSnapshot.val())
              .map(([id, request]) => ({
                id,
                ...request,
              }))
              .sort((a, b) => parseInt(a.id) - parseInt(b.id))
          : [];
        
        // Identify new requests by comparing with previous state
        setWithdrawRequests((prev) => {
          const prevIds = prev.map((req) => req.id);
          const newIds = newRequests
            .filter((req) => !prevIds.includes(req.id))
            .map((req) => req.id);
          setNewRequestIds(newIds);
          return newRequests;
        });

        // Process tasks and sort by taskId
        setTasks(
          tasksSnapshot.exists() 
            ? Object.entries(tasksSnapshot.val())
                .map(([id, task]) => ({ taskId: parseInt(id), ...task }))
                .sort((a, b) => a.taskId - b.taskId)
            : []
        );

        // Process gifts and sort by giftId
        setGifts(
          giftsSnapshot.exists()
            ? Object.entries(giftsSnapshot.val())
                .map(([id, gift]) => ({ giftId: parseInt(id), ...gift }))
                .sort((a, b) => a.giftId - b.giftId)
            : []
        );

      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message || 'Failed to fetch data');
        toast.error('Failed to load user data');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [userId]);

  // Fetch ads for each video
  useEffect(() => {
    const fetchAdsForVideos = async () => {
      if (!videos.length) return;
      const sanitizedMobile = userId.replace(/[^0-9]/g, '_');
      const adsMap = {};
      await Promise.all(videos.map(async (video) => {
        if (!video.id) return;
        const videoKey = video.dbKey || video.id;
        const adsRef = dbRef(database, `videoplayer/videoUpload/${sanitizedMobile}/${videoKey}/ads`);
        const adsSnap = await get(adsRef);
        if (adsSnap.exists()) {
          adsMap[video.id] = Object.values(adsSnap.val());
        }
      }));
      setVideoAds(adsMap);
    };
    fetchAdsForVideos();
  }, [videos, userId]);

  // Auto-scroll to new requests
  useEffect(() => {
    if (newRequestIds.length > 0 && sliderRef.current) {
      const newRequestIndex = withdrawRequests.findIndex((req) => req.id === newRequestIds[0]);
      if (newRequestIndex !== -1) {
        sliderRef.current.slickGoTo(newRequestIndex);
        const interval = setInterval(() => {
          sliderRef.current.slickNext();
        }, 4000);
        return () => clearInterval(interval);
      }
    }
  }, [newRequestIds, withdrawRequests]);

  // Media modal handlers
  const openMediaModal = (mediaUrl, mediaType) => {
    setMediaModal({ isOpen: true, mediaUrl, mediaType });
  };

  const closeMediaModal = () => {
    setMediaModal({ isOpen: false, mediaUrl: null, mediaType: null });
  };

  // Handle task completion status
  const handleTaskCompletion = async (taskId, isCompleted) => {
    try {
      const sanitizedMobile = userId.replace(/[^0-9]/g, '_');
      const taskRef = dbRef(database, `${FIREBASE_PATHS.TASKS}/${sanitizedMobile}/${taskId}`);
      
      const updatedTask = {
        ...tasks.find(task => task.taskId === taskId),
        status: isCompleted ? 'completed' : 'pending',
        completedAt: isCompleted ? new Date().toISOString() : null,
        checked: isCompleted // Store checkbox status in task node
      };

      await set(taskRef, updatedTask);
      
      // If task is completed, also store it in the user's completed tasks node
      if (isCompleted) {
        const userCompletedTasksRef = dbRef(database, `${FIREBASE_PATHS.USERS}/${sanitizedMobile}/completedTasks/${taskId}`);
        await set(userCompletedTasksRef, {
          ...updatedTask,
          completedBy: 'admin', // Track who marked it as completed
          completedAt: new Date().toISOString()
        });
      } else {
        // If task is marked as pending, remove it from completed tasks
        const userCompletedTasksRef = dbRef(database, `${FIREBASE_PATHS.USERS}/${sanitizedMobile}/completedTasks/${taskId}`);
        await remove(userCompletedTasksRef);
      }
      
      setTasks(prev => prev.map(task => 
        task.taskId === taskId ? updatedTask : task
      ));
      
      toast.success(`Task ${taskId} marked as ${isCompleted ? 'completed' : 'pending'}!`);
    } catch (err) {
      console.error('Error updating task completion status:', err);
      toast.error('Failed to update task status');
    }
  };

  // Handle purchase and sale checkboxes
  const handlePurchaseSaleCheck = async (taskId, type, isChecked) => {
    try {
      const sanitizedMobile = userId.replace(/[^0-9]/g, '_');
      const taskRef = dbRef(database, `${FIREBASE_PATHS.TASKS}/${sanitizedMobile}/${taskId}`);
      
      const currentTask = tasks.find(task => task.taskId === taskId);
      const updatedTask = {
        ...currentTask,
        [type]: isChecked // Store purchase or sale status in task node
      };

      await set(taskRef, updatedTask);
      
      setTasks(prev => prev.map(task => 
        task.taskId === taskId ? updatedTask : task
      ));
      
      toast.success(`Task ${taskId} ${type} marked as ${isChecked ? 'completed' : 'pending'}!`);
    } catch (err) {
      console.error('Error updating task purchase/sale status:', err);
      toast.error('Failed to update task status');
    }
  };

  // Approve media handler
  const handleApprove = async (itemId, type) => {
    try {
      const sanitizedMobile = userId.replace(/[^0-9]/g, '_');
      const mediaArray = type === 'photo' ? photos : videos;
      const mediaItem = mediaArray.find(item => item.id === itemId);
      
      if (!mediaItem) {
        toast.error(`${type === 'photo' ? 'Photo' : 'Video'} not found`);
        return;
      }

      // Use dbKey if available, otherwise use id
      const itemKey = mediaItem.dbKey || itemId;
      const path = `${type === 'photo' ? FIREBASE_PATHS.PHOTO_UPLOAD : FIREBASE_PATHS.VIDEO_UPLOAD}/${sanitizedMobile}/${itemKey}`;
      
      await set(dbRef(database, path), {
        ...mediaItem,
        status: 'approved'
      });

      if (type === 'photo') {
        setPhotos(prev => prev.map(photo => 
          photo.id === itemId ? { ...photo, status: 'approved' } : photo
        ));
      } else {
        setVideos(prev => prev.map(video => 
          video.id === itemId ? { ...video, status: 'approved' } : video
        ));
      }

      toast.success(`${type === 'photo' ? 'Photo' : 'Video'} approved successfully!`);
    } catch (err) {
      console.error('Approval error:', err);
      toast.error(`Failed to approve ${type}`);
    }
  };

  // Task form handlers
  const handleTaskSubmit = async (e) => {
    e.preventDefault();
    if (!taskForm.title.trim() || !taskForm.description.trim()) {
      toast.error('Please fill all task fields');
      return;
    }

    try {
      const sanitizedMobile = userId.replace(/[^0-9]/g, '_');
      const tasksRef = dbRef(database, `${FIREBASE_PATHS.TASKS}/${sanitizedMobile}`);
      let imageUrl = null;

      // If a photo is selected, upload it to Firebase Storage
      if (taskPhoto) {
        const firstWord = taskForm.title.trim().split(/\s+/)[0] || 'task';
        const ext = taskPhoto.name.split('.').pop() || 'jpg';
        const imageName = `${firstWord}.${ext}`;
        const storagePath = `videoplayer/Task/${sanitizedMobile}/${imageName}`;
        const imageRef = storageRef(storage, storagePath); // correct usage
        await uploadBytes(imageRef, taskPhoto);
        imageUrl = await getDownloadURL(imageRef);
      }

      if (isEditingTask) {
        // Update existing task
        const taskRef = dbRef(database, `${FIREBASE_PATHS.TASKS}/${sanitizedMobile}/${taskForm.taskId}`);
        const updatedTask = {
          taskId: taskForm.taskId,
          title: taskForm.title,
          description: taskForm.description,
          withdrawRequestId: taskForm.withdrawRequestId || null,
          assignedAt: taskForm.assignedAt, // Preserve original timestamp
          status: taskForm.status || 'pending',
          completedAt: taskForm.completedAt || null,
          imageUrl: imageUrl || taskForm.imageUrl || null
        };

        await set(taskRef, updatedTask);
        setTasks(prev => prev
          .map(task => task.taskId === taskForm.taskId ? updatedTask : task)
          .sort((a, b) => a.taskId - b.taskId)
        );
        toast.success(`Task ${taskForm.taskId} updated successfully!`);
      } else {
        // Create new task
        const tasksSnapshot = await get(tasksRef);
        const existingTasks = tasksSnapshot.exists() 
          ? Object.keys(tasksSnapshot.val()).map(key => parseInt(key))
          : [];
        const nextTaskId = existingTasks.length > 0 ? Math.max(...existingTasks) + 1 : 1;

        const newTask = {
          taskId: nextTaskId,
          title: taskForm.title,
          description: taskForm.description,
          withdrawRequestId: taskForm.withdrawRequestId || null,
          assignedAt: new Date().toISOString(),
          status: 'pending',
          completedAt: null,
          imageUrl: imageUrl || null
        };

        await set(dbRef(database, `${FIREBASE_PATHS.TASKS}/${sanitizedMobile}/${nextTaskId}`), newTask);
        setTasks(prev => [...prev, newTask].sort((a, b) => a.taskId - b.taskId));
        toast.success(`Task ${nextTaskId} assigned successfully!`);
      }

      setTaskForm({ taskId: '', title: '', description: '', withdrawRequestId: '' });
      setTaskPhoto(null);
      setShowTaskForm(false);
      setIsEditingTask(false);
    } catch (err) {
      console.error('Task operation error:', err);
      toast.error(`Failed to ${isEditingTask ? 'update' : 'assign'} task`);
    }
  };

  // Gift form handlers
  const handleGiftSubmit = async (e) => {
    e.preventDefault();
    if (!giftForm.giftName.trim()) {
      toast.error('Please fill the gift name');
      return;
    }

    try {
      const sanitizedMobile = userId.replace(/[^0-9]/g, '_');
      const giftsRef = dbRef(database, `${FIREBASE_PATHS.GIFTS}/${sanitizedMobile}/gifts`);
      
      if (isEditingGift) {
        // Update existing gift
        const giftRef = dbRef(database, `${FIREBASE_PATHS.GIFTS}/${sanitizedMobile}/gifts/${giftForm.giftId}`);
        const updatedGift = {
          giftId: giftForm.giftId,
          giftName: giftForm.giftName,
          assignedAt: giftForm.assignedAt, // Preserve original timestamp
          updatedAt: new Date().toISOString()
        };

        await set(giftRef, updatedGift);
        
        setGifts(prev => prev
          .map(gift => gift.giftId === giftForm.giftId ? updatedGift : gift)
          .sort((a, b) => a.giftId - b.giftId)
        );
        
        toast.success(`Gift ${giftForm.giftId} updated successfully!`);
      } else {
        // Create new gift
        const giftsSnapshot = await get(giftsRef);
        const existingGifts = giftsSnapshot.exists() 
          ? Object.keys(giftsSnapshot.val()).map(key => parseInt(key))
          : [];
        const nextGiftId = existingGifts.length > 0 ? Math.max(...existingGifts) + 1 : 1;

        const newGift = {
          giftId: nextGiftId,
          giftName: giftForm.giftName,
          assignedAt: new Date().toISOString(),
          updatedAt: null
        };

        await set(dbRef(database, `${FIREBASE_PATHS.GIFTS}/${sanitizedMobile}/gifts/${nextGiftId}`), newGift);
        
        setGifts(prev => [...prev, newGift].sort((a, b) => a.giftId - b.giftId));
        toast.success(`Gift ${nextGiftId} assigned successfully!`);
      }

      setGiftForm({ giftId: '', giftName: '' });
      setShowGiftForm(false);
      setIsEditingGift(false);
    } catch (err) {
      console.error('Gift operation error:', err);
      toast.error(`Failed to ${isEditingGift ? 'update' : 'assign'} gift`);
    }
  };

  // Open task form for creating new task
  const openTaskForm = (requestId) => {
    setTaskForm({ taskId: '', title: '', description: '', withdrawRequestId: requestId });
    setIsEditingTask(false);
    setShowTaskForm(true);
  };

  // Open task form for editing existing task
  const openEditTaskForm = (task) => {
    setTaskForm({
      taskId: task.taskId,
      title: task.title,
      description: task.description,
      withdrawRequestId: task.withdrawRequestId || '',
      assignedAt: task.assignedAt,
      status: task.status,
      completedAt: task.completedAt || null
    });
    setIsEditingTask(true);
    setShowTaskForm(true);
  };

  // Close task form
  const closeTaskForm = () => {
    setTaskForm({ taskId: '', title: '', description: '', withdrawRequestId: '' });
    setIsEditingTask(false);
    setShowTaskForm(false);
  };

  // Open gift form for creating new gift
  const openGiftForm = () => {
    setGiftForm({ giftId: '', giftName: '' });
    setIsEditingGift(false);
    setShowGiftForm(true);
  };

  // Open gift form for editing existing gift
  const openEditGiftForm = (gift) => {
    setGiftForm({
      giftId: gift.giftId,
      giftName: gift.giftName,
      assignedAt: gift.assignedAt,
      updatedAt: gift.updatedAt || null
    });
    setIsEditingGift(true);
    setShowGiftForm(true);
  };

  // Close gift form
  const closeGiftForm = () => {
    setGiftForm({ giftId: '', giftName: '' });
    setIsEditingGift(false);
    setShowGiftForm(false);
  };

  // Give Points Modal Handlers
  const openGivePointsModal = (taskId) => {
    setGivePointsTaskId(taskId);
    setGivePointsValue('');
    setShowGivePointsModal(true);
  };
  const closeGivePointsModal = () => {
    setShowGivePointsModal(false);
    setGivePointsTaskId(null);
    setGivePointsValue('');
  };
  const handleGivePointsChange = (e) => {
    // Only allow numbers
    const value = e.target.value.replace(/[^0-9]/g, '');
    setGivePointsValue(value);
  };
  const handleGivePointsSubmit = async (e) => {
    e.preventDefault();
    const pointsToAdd = parseInt(givePointsValue, 10);
    if (!pointsToAdd || pointsToAdd <= 0) {
      toast.error('Please enter a valid number of points');
      return;
    }
    setGivePointsLoading(true);
    try {
      const sanitizedMobile = userId.replace(/[^0-9]/g, '_');
      const userRef = dbRef(database, `${FIREBASE_PATHS.USERS}/${sanitizedMobile}`);
      const userSnapshot = await get(userRef);
      let currentPoints = 0;
      let currentPurchasePoints = 0;
      if (userSnapshot.exists()) {
        const userData = userSnapshot.val();
        currentPoints = parseInt(userData.points, 10) || 0;
        currentPurchasePoints = parseInt(userData.purchasePoints, 10) || 0;
      }
      const newPoints = currentPoints + pointsToAdd;
      const newPurchasePoints = currentPurchasePoints + pointsToAdd;
      await update(userRef, { points: newPoints, purchasePoints: newPurchasePoints });
      setUser((prev) => ({ ...prev, points: newPoints, purchasePoints: newPurchasePoints }));
      toast.success(`Added ${pointsToAdd} points to user!`);
      closeGivePointsModal();
    } catch (err) {
      toast.error('Failed to give points');
      console.error(err);
    } finally {
      setGivePointsLoading(false);
    }
  };

  // Function to fetch deposits
  const fetchDeposits = async () => {
    setDepositsLoading(true);
    setDepositsError('');
    try {
      const sanitizedMobile = userId.replace(/[^0-9]/g, '_');
      const depositsRef = dbRef(database, `videoplayer/Users/${sanitizedMobile}/deposite`);
      const snapshot = await get(depositsRef);
      if (snapshot.exists()) {
        const data = snapshot.val();
        const depositList = Object.entries(data).map(([id, dep]) => ({
          id,
          ...dep,
        })).sort((a, b) => parseInt(a.id) - parseInt(b.id));
        setDeposits(depositList);
      } else {
        setDeposits([]);
      }
    } catch (err) {
      setDepositsError('Failed to fetch deposits');
      setDeposits([]);
    } finally {
      setDepositsLoading(false);
    }
  };

  // Function to fetch invite batches
  const fetchInviteBatches = async () => {
    setInviteLoading(true);
    try {
      const sanitizedMobile = userId.replace(/[^0-9]/g, '_');
      const inviteRef = dbRef(database, `/videoplayer/Users/${sanitizedMobile}/invite`);
      const snapshot = await get(inviteRef);
      if (snapshot.exists()) {
        const data = snapshot.val();
        const batches = Object.entries(data).map(([batchName, batchInvites]) => ({
          batchName,
          points: batchInvites.points || 0,
          invites: Object.entries(batchInvites)
            .filter(([key]) => key !== 'points')
            .sort((a, b) => Number(a[0]) - Number(b[0]))
            .map(([num, invite]) => ({ ...invite, key: num }))
        }));
        batches.sort((a, b) => {
          const aNum = parseInt(a.batchName.replace('invite', ''));
          const bNum = parseInt(b.batchName.replace('invite', ''));
          return aNum - bNum;
        });
        setInviteBatches(batches);
        setSelectedInviteBatchIdx(0);
      } else {
        setInviteBatches([]);
      }
    } catch (err) {
      setInviteBatches([]);
    }
    setInviteLoading(false);
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  // Slider settings for withdrawal requests
  const sliderSettings = {
    dots: true,
    infinite: withdrawRequests.length > 1,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    adaptiveHeight: true,
    className: 'withdrawal-slider',
  };

  // Remove ads handler
  const handleRemoveAds = async (videoId) => {
    try {
      const sanitizedMobile = userId.replace(/[^0-9]/g, '_');
      const video = videos.find(v => v.id === videoId);
      if (!video) {
        toast.error('Video not found');
        return;
      }
      
      const videoKey = video.dbKey || videoId;
      const adsRef = dbRef(database, `videoplayer/videoUpload/${sanitizedMobile}/${videoKey}/ads`);
      await remove(adsRef);
      setVideoAds(prev => {
        const updated = { ...prev };
        delete updated[videoId];
        return updated;
      });
      toast.success('All ads removed for this video!');
    } catch (err) {
      toast.error('Failed to remove ads');
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
          className="h-16 w-16 border-4 border-indigo-500 border-t-transparent rounded-full"
        />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="bg-white p-6 rounded-xl shadow-lg max-w-md w-full text-center">
          <div className="text-red-500 mb-4">
            <FiX className="inline-block text-4xl" />
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Error Loading Profile</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Calculate task stats
  const taskStats = {
    total: tasks.length,
    pending: tasks.filter(t => t.status === 'pending').length,
    completed: tasks.filter(t => t.status === 'completed').length
  };

  const handleInvitePointsChange = (inviteKey, value) => {
    setInvitePoints(prev => ({
      ...prev,
      [inviteKey]: value.replace(/[^0-9]/g, '') // Only allow numbers
    }));
  };

  const handleGiveInvitePoints = async (mobile, inviteKey) => {
    const pointsToAdd = parseInt(invitePoints[inviteKey], 10);
    if (!pointsToAdd || pointsToAdd <= 0) {
      toast.error('Please enter a valid number of points');
      return;
    }
    setInvitePointsLoading(prev => ({ ...prev, [inviteKey]: true }));
    try {
      const sanitizedMobile = (mobile || '').replace(/[^0-9]/g, '_');
      const userRef = dbRef(database, `videoplayer/Users/${sanitizedMobile}`);
      const userSnapshot = await get(userRef);
      let currentPoints = 0;
      if (userSnapshot.exists()) {
        const userData = userSnapshot.val();
        currentPoints = parseInt(userData.points, 10) || 0;
      }
      const newPoints = currentPoints + pointsToAdd;
      await update(userRef, { points: newPoints });
      toast.success(`Added ${pointsToAdd} points to user ${mobile}!`);
      setInvitePoints(prev => ({ ...prev, [inviteKey]: '' }));
    } catch (err) {
      toast.error('Failed to give points');
    }
    setInvitePointsLoading(prev => ({ ...prev, [inviteKey]: false }));
  };

  const handleBatchPointsChange = (value) => {
    setBatchPoints(value.replace(/[^0-9]/g, ''));
  };

  const handleGiveBatchPoints = async () => {
    const pointsToAdd = parseInt(batchPoints, 10);
    if (!pointsToAdd || pointsToAdd <= 0) {
      toast.error('Please enter a valid number of points');
      return;
    }
    setBatchPointsLoading(true);
    try {
      const currentBatch = inviteBatches[selectedInviteBatchIdx];
      if (!currentBatch || !currentBatch.invites.length) {
        toast.error('No users in this invite list');
        setBatchPointsLoading(false);
        return;
      }
      // Add points to each user's points node (increment)
      for (const invite of currentBatch.invites) {
        const mobile = invite.mobile;
        const sanitizedMobile = (mobile || '').replace(/[^0-9]/g, '_');
        const userRef = dbRef(database, `videoplayer/Users/${sanitizedMobile}`);
        const userSnapshot = await get(userRef);
        let currentPoints = 0;
        if (userSnapshot.exists()) {
          const userData = userSnapshot.val();
          currentPoints = parseInt(userData.points, 10) || 0;
        }
        const newPoints = currentPoints + pointsToAdd;
        // Update the user's points node
        await update(userRef, { points: newPoints });
      }
      // Store the points value in the batch node
      const sanitizedMobile = userId.replace(/[^0-9]/g, '_');
      const batchName = currentBatch.batchName;
      const batchPointsRef = dbRef(database, `/videoplayer/Users/${sanitizedMobile}/invite/${batchName}/points`);
      await set(batchPointsRef, pointsToAdd);

      toast.success(`Added ${pointsToAdd} points to all users in this invite list!`);
      setBatchPoints('');
      // Refresh the invite batches to show updated points
      fetchInviteBatches();
    } catch (err) {
      toast.error('Failed to give points');
    }
    setBatchPointsLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-gradient-to-r from-indigo-600 to-purple-600 shadow-lg w-full">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-3 sm:py-4 w-full">
          <div className="flex items-center justify-between w-full">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-white hover:text-indigo-200 transition-colors text-base sm:text-lg"
            >
              <FiArrowLeft className="mr-2" />
              {/* <span className="font-medium">Back</span> */}
            </button>
            <h1 className="text-lg sm:text-2xl font-bold text-white text-center">
              User Profile
            </h1>
            <div className="w-6 sm:w-8"></div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-4 sm:py-8 w-full">
        {/* User Profile Card */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-xl shadow-md overflow-hidden mb-6 sm:mb-8 w-full"
        >
          <div className="flex flex-col md:flex-row w-full">
            <div className="md:flex-shrink-0 p-4 sm:p-6 flex justify-center">
              {user.profilePictureUrl ? (
                <motion.img
                  src={user.profilePictureUrl}
                  alt={`${user.name} ${user.surname}`}
                  className="h-24 w-24 sm:h-32 sm:w-32 rounded-full object-cover border-4 border-indigo-100 shadow-md"
                  whileHover={{ scale: 1.05 }}
                  transition={{ type: "spring", stiffness: 400 }}
                />
              ) : (
                <div className="h-24 w-24 sm:h-32 sm:w-32 rounded-full bg-indigo-100 flex items-center justify-center border-4 border-indigo-200">
                  <FiUsers className="text-indigo-400 text-4xl" />
                </div>
              )}
            </div>
            <div className="p-4 sm:p-6 md:p-8 flex-1 w-full">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between w-full">
                <div>
                  <h2 className="text-lg sm:text-2xl font-bold text-gray-800">
                    {user.name} {user.surname}
                  </h2>
                </div>
                <div className="mt-4 md:mt-0 flex items-center">
                  <div className="bg-gradient-to-r from-amber-400 to-amber-500 text-white px-4 py-2 rounded-full flex items-center shadow-lg">
                    <FaRupeeSign className="mr-2" />
                    <span className="font-bold">{user.points} Points</span>
                  </div>
                </div>
              </div>

              <div className="mt-4 sm:mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-center">
                  <div className="bg-indigo-100 p-2 rounded-full mr-3">
                    <FiBriefcase className="text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500">Business</p>
                    <p className="text-gray-600 text-sm sm:text-base">{user.business}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="bg-indigo-100 p-2 rounded-full mr-3">
                    <FiFileText className="text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500">Plan</p>
                    <p className="font-medium text-sm sm:text-base">{user.plan}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="bg-indigo-100 p-2 rounded-full mr-3">
                    <FiAward className="text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500">GST</p>
                    <p className="font-medium text-sm sm:text-base">{user.gst}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="bg-indigo-100 p-2 rounded-full mr-3">
                    <FiUsers className="text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500">Mobile</p>
                    <p className="font-medium text-sm sm:text-base">{user.mobile}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="bg-indigo-100 p-2 rounded-full mr-3">
                    <FiMail className="text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500">Email</p>
                    <p className="font-medium text-sm sm:text-base">{user.email}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Tab Navigation */}
        <div className="mb-4 sm:mb-6 w-full overflow-x-auto">
          <nav className="flex space-x-2 sm:space-x-4 overflow-x-auto pb-1">
            <button
              onClick={() => setActiveTab('profile')}
              className={`px-3 sm:px-4 py-2 rounded-t-lg font-medium flex items-center whitespace-nowrap text-sm sm:text-base ${
                activeTab === 'profile'
                  ? 'bg-white text-indigo-600 border-b-2 border-indigo-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <FiUser className="mr-2" />
              Profile
            </button>
            <button
              onClick={() => setActiveTab('photos')}
              className={`px-3 sm:px-4 py-2 rounded-t-lg font-medium flex items-center whitespace-nowrap text-sm sm:text-base ${
                activeTab === 'photos'
                  ? 'bg-white text-indigo-600 border-b-2 border-indigo-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <FiImage className="mr-2" />
              Photos
            </button>
            <button
              onClick={() => setActiveTab('videos')}
              className={`px-3 sm:px-4 py-2 rounded-t-lg font-medium flex items-center whitespace-nowrap text-sm sm:text-base ${
                activeTab === 'videos'
                  ? 'bg-white text-indigo-600 border-b-2 border-indigo-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <FiVideo className="mr-2" />
              Videos
            </button>
            <button
              onClick={() => setActiveTab('withdraw')}
              className={`px-3 sm:px-4 py-2 rounded-t-lg font-medium flex items-center whitespace-nowrap text-sm sm:text-base ${
                activeTab === 'withdraw'
                  ? 'bg-white text-indigo-600 border-b-2 border-indigo-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <FaRupeeSign className="mr-2" />
              Withdraw & Tasks
            </button>
            <button
              onClick={() => setActiveTab('gifts')}
              className={`px-3 sm:px-4 py-2 rounded-t-lg font-medium flex items-center whitespace-nowrap text-sm sm:text-base ${
                activeTab === 'gifts'
                  ? 'bg-white text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              <FiGift className="mr-2" />
              Extra Commission
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden w-full">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
              className="p-4 sm:p-8"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Basic Info */}
                <div className="bg-white rounded-xl shadow p-6">
                  <h3 className="text-lg font-bold text-indigo-700 flex items-center mb-4">
                    <FiUser className="mr-2" /> Basic Information
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <FiPhone className="text-indigo-400 mr-2" />
                      <span className="text-gray-500 mr-2">Mobile:</span>
                      <span className="font-semibold">{user.mobile}</span>
                    </div>
                    <div className="flex items-center">
                      <FiBriefcase className="text-indigo-400 mr-2" />
                      <span className="text-gray-500 mr-2">Business:</span>
                      <span className="font-semibold">{user.business}</span>
                    </div>
                    <div className="flex items-center">
                      <FiAward className="text-indigo-400 mr-2" />
                      <span className="text-gray-500 mr-2">GST:</span>
                      <span className="font-semibold">{user.gst}</span>
                    </div>
                    <div className="flex items-center">
                      <FiFileText className="text-indigo-400 mr-2" />
                      <span className="text-gray-500 mr-2">Visit Branch:</span>
                      <span className="font-semibold">{user.visitBranch}</span>
                    </div>
                    <div className="flex items-center">
                      <FiClock className="text-indigo-400 mr-2" />
                      <span className="text-gray-500 mr-2">Registered:</span>
                      <span className="font-semibold">{formatTimestamp(user.timestamp)}</span>
                    </div>
                  </div>
                </div>

                {/* Subscription & Payment */}
                <div className="bg-white rounded-xl shadow p-6">
                  <h3 className="text-lg font-bold text-indigo-700 flex items-center mb-4">
                    <FiCreditCard className="mr-2" /> Subscription & Payment
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <FiFileText className="text-indigo-400 mr-2" />
                      <span className="text-gray-500 mr-2">Plan:</span>
                      <span className="font-semibold">{user.plan}</span>
                    </div>
                    <div className="flex items-center">
                      <FiCreditCard className="text-indigo-400 mr-2" />
                      <span className="text-gray-500 mr-2">Payment:</span>
                      <span className="font-semibold capitalize">{user.payment}</span>
                    </div>
                                         <div className="flex items-center">
                       <FaRupeeSign className="text-amber-500 mr-2" />
                       <span className="text-gray-500 mr-2">Points:</span>
                       <span className="font-semibold text-amber-600">{user.points}</span>
                     </div>
                    <button
                      onClick={() => {
                        setShowInviteModal(true);
                        fetchInviteBatches();
                      }}
                      className="mt-4 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                    >
                      <FiUsers className="mr-2" />
                      Show Invite List
                    </button>
                  </div>
                </div>
              </div>

              {/* Payment Screenshot */}
              {user.screenshotUrl && (
                <div className="mt-8 bg-white rounded-xl shadow p-6 flex flex-col items-center">
                  <h3 className="text-lg font-bold text-indigo-700 flex items-center mb-4">
                    <FiCamera className="mr-2" /> Payment Screenshot
                  </h3>
                  <div
                    className="relative cursor-pointer hover:scale-105 transition-transform duration-200"
                    onClick={() => openMediaModal(user.screenshotUrl, 'image')}
                  >
                    <img
                      src={user.screenshotUrl}
                      alt="Payment Screenshot"
                      className="max-w-full h-64 object-contain rounded-lg shadow-lg border border-gray-200"
                      onError={e => { e.target.src = 'https://via.placeholder.com/400x300?text=Screenshot+Not+Available'; }}
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-all duration-200 rounded-lg flex items-center justify-center">
                      <span className="text-white opacity-0 hover:opacity-100 transition-opacity font-medium">
                        Click to view full size
                      </span>
                    </div>
                  </div>
                  <p className="text-center text-sm text-gray-600 mt-2">
                    Click on the image to view in full size
                  </p>
                </div>
              )}
            </motion.div>
          )}

          {/* Photos Tab */}
          {activeTab === 'photos' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
              className="p-4 sm:p-6"
            >
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <FiImage className="mr-2 text-indigo-500" />
                User Photos
              </h3>
              
              {photos.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {photos.map((photo) => (
                    <motion.div
                      key={photo.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                      className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                    >
                      <div 
                        className="relative h-48 bg-gray-100 cursor-pointer"
                        onClick={() => openMediaModal(photo.photoURL, 'image')}
                      >
                        <img
                          src={photo.photoURL}
                          alt={photo.title}
                          className="w-full h-full object-cover"
                          onError={(e) => (e.target.src = 'https://via.placeholder.com/300x200?text=Image+Not+Available')}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 hover:opacity-100 transition-opacity flex items-end p-4">
                          <span className="text-white font-medium">{photo.title || 'Untitled'}</span>
                        </div>
                      </div>
                      <div className="p-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm text-gray-500">
                            <FiClock className="inline mr-1" />
                            {formatTimestamp(photo.uploadDate)}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            photo.status === 'approved' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-amber-100 text-amber-800'
                          }`}>
                            {photo.status === 'approved' ? 'Approved' : 'Pending'}
                          </span>
                        </div>
                        {/* Engagement Stats */}
                        <div className="flex items-center gap-3 mb-3 text-sm">
                          <div className="flex items-center text-green-600">
                            <FiThumbsUp className="mr-1" />
                            <span>{photoEngagement[photo.id]?.likes || 0}</span>
                          </div>
                          <div className="flex items-center text-blue-600">
                            <FiShare2 className="mr-1" />
                            <span>{photoEngagement[photo.id]?.shares || 0}</span>
                          </div>
                        </div>
                        {/* Show Engagement Users Button */}
                        <button
                          onClick={() => navigate('/admin/showlikeunlikeusers', { 
                            state: { 
                              type: 'photo', 
                              mediaId: photo.id, 
                              mediaKey: photo.dbKey || photo.id,
                              userMobile: userId,
                              mediaTitle: photo.title || 'Untitled Photo'
                            } 
                          })}
                          className="w-full py-2 rounded-lg flex items-center justify-center bg-gradient-to-r from-purple-600 to-purple-700 text-white hover:from-purple-700 hover:to-purple-800 transition-all duration-200 shadow-md hover:shadow-lg mb-2"
                        >
                          <FiUsers className="mr-2" />
                          Show Like & Share Users
                        </button>
                        <button
                          onClick={() => handleApprove(photo.id, 'photo')}
                          disabled={photo.status === 'approved'}
                          className={`w-full py-2 rounded-lg flex items-center justify-center ${
                            photo.status === 'approved'
                              ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                              : 'bg-indigo-600 text-white hover:bg-indigo-700'
                          }`}
                        >
                          <FiCheckCircle className="mr-2" />
                          {photo.status === 'approved' ? 'Already Approved' : 'Approve Photo'}
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FiImage className="mx-auto text-4xl text-gray-400 mb-2" />
                  <p className="text-gray-500">No photos uploaded yet</p>
                </div>
              )}
            </motion.div>
          )}

          {/* Videos Tab */}
          {activeTab === 'videos' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
              className="p-4 sm:p-6"
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                  <FiVideo className="mr-2 text-indigo-500" />
                  User Videos
                </h3>
                <div className="flex items-center bg-gradient-to-r from-green-500 to-green-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                  <FiUsers className="mr-1" />
                  {subscriberCount} Subscribers
                </div>
              </div>
              
              {videos.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {videos.map((video) => (
                    <motion.div
                      key={video.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                      className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                    >
                      <div 
                        className="relative h-48 bg-gray-100 cursor-pointer"
                        onClick={() => openMediaModal(video.videoURL, 'video')}
                      >
                        {video.thumbnailURL ? (
                          <img
                            src={video.thumbnailURL}
                            alt={video.title}
                            className="w-full h-full object-cover"
                            onError={(e) => (e.target.src = 'https://via.placeholder.com/300x200?text=Thumbnail+Not+Available')}
                          />
                        ) : (
                          <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                            <FiVideo className="text-4xl text-gray-400" />
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 hover:opacity-100 transition-opacity flex items-end p-4">
                          <span className="text-white font-medium">{video.title || 'Untitled'}</span>
                        </div>
                      </div>
                      <div className="p-4">
                        <div className="mb-2">
                          <p className="text-sm text-gray-600 line-clamp-2">
                            {video.description || 'No description'}
                          </p>
                        </div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm text-gray-500">
                            <FiClock className="inline mr-1" />
                            {formatTimestamp(video.uploadDate)}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            video.status === 'approved' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-amber-100 text-amber-800'
                          }`}>
                            {video.status === 'approved' ? 'Approved' : 'Pending'}
                          </span>
                        </div>
                        {/* Engagement Stats */}
                        <div className="flex items-center gap-3 mb-3 text-sm">
                          <div className="flex items-center text-green-600">
                            <FiThumbsUp className="mr-1" />
                            <span>{videoEngagement[video.id]?.likes || 0}</span>
                          </div>
                          <div className="flex items-center text-red-600">
                            <FiThumbsDown className="mr-1" />
                            <span>{videoEngagement[video.id]?.dislikes || 0}</span>
                          </div>
                          <div className="flex items-center text-blue-600">
                            <FiShare2 className="mr-1" />
                            <span>{videoEngagement[video.id]?.shares || 0}</span>
                          </div>
                        </div>
                        {/* Show Engagement Users Button */}
                        <button
                          onClick={() => navigate('/admin/showlikeunlikeusers', { 
                            state: { 
                              type: 'video', 
                              mediaId: video.id, 
                              mediaKey: video.dbKey || video.id,
                              userMobile: userId,
                              mediaTitle: video.title || 'Untitled Video',
                              hasSubscribers: true
                            } 
                          })}
                          className="w-full py-2 rounded-lg flex items-center justify-center bg-gradient-to-r from-purple-600 to-purple-700 text-white hover:from-purple-700 hover:to-purple-800 transition-all duration-200 shadow-md hover:shadow-lg mb-2"
                        >
                          <FiUsers className="mr-2" />
                          Show Like, Unlike, Share & Subscribers
                        </button>
                        {/* Show assigned ads if present */}
                        {videoAds[video.id] && videoAds[video.id].length > 0 && (
                          <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded">
                            <div className="font-semibold text-blue-700 mb-2 flex items-center">
                              <FiVideo className="mr-1" /> Ads assigned to this video:
                            </div>
                            <div className="space-y-2">
                              {videoAds[video.id].map((ad) => (
                                <div key={ad.adId} className="flex flex-col sm:flex-row items-center gap-2 border-b border-blue-100 pb-2 last:border-b-0">
                                  <video src={ad.adUrl} controls className="w-full sm:w-40 rounded shadow border border-blue-200" style={{ maxHeight: 100 }} />
                                  <div className="flex-1 text-xs text-blue-900">
                                    <div>Ad Type: {ad.adType}</div>
                                    <div>Duration: {ad.durationValue} {ad.durationType}</div>
                                    <div>Uploaded: {ad.uploadedAt ? formatTimestamp(ad.uploadedAt) : ''}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        <div className="flex gap-2 mt-2">
                          <button
                            onClick={() => navigate('/admin/videoads', { state: { video, userMobile: user.mobile } })}
                            className="flex-1 py-2 rounded-lg flex items-center justify-center bg-blue-600 text-white hover:bg-blue-700"
                          >
                            <FiPlus className="mr-2" />
                            Give Ads
                          </button>
                          <button
                            onClick={() => handleRemoveAds(video.id)}
                            disabled={!videoAds[video.id] || videoAds[video.id].length === 0}
                            className={`flex-1 py-2 rounded-lg flex items-center justify-center ml-2 ${
                              videoAds[video.id] && videoAds[video.id].length > 0
                                ? 'bg-red-600 text-white hover:bg-red-700'
                                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            }`}
                          >
                            <FiX className="mr-2" />
                            Remove Ads
                          </button>
                        </div>
                        <button
                          onClick={() => handleApprove(video.id, 'video')}
                          disabled={video.status === 'approved'}
                          className={`w-full py-2 rounded-lg flex items-center justify-center mt-2 ${
                            video.status === 'approved'
                              ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                              : 'bg-indigo-600 text-white hover:bg-indigo-700'
                          }`}
                        >
                          <FiCheckCircle className="mr-2" />
                          {video.status === 'approved' ? 'Already Approved' : 'Approve Video'}
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FiVideo className="mx-auto text-4xl text-gray-400 mb-2" />
                  <p className="text-gray-500">No videos uploaded yet</p>
                </div>
              )}
            </motion.div>
          )}

          {/* Withdraw & Tasks Tab */}
          {activeTab === 'withdraw' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
              className="p-4 sm:p-6"
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Withdrawal Section */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <FaRupeeSign className="mr-2 text-indigo-500" />
                    Withdrawal Requests
                  </h3>
                  
                  {withdrawRequests.length > 0 ? (
                    <Slider ref={sliderRef} {...sliderSettings}>
                      {withdrawRequests.map((request) => (
                        <div key={request.id} className="px-2">
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3 }}
                            className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                          >
                            <div className="space-y-2">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Request ID:</span>
                                <span className="font-medium">{request.id}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Mobile:</span>
                                <span className="font-medium">{request.mobile}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Name:</span>
                                <span className="font-medium">{request.name}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Surname:</span>
                                <span className="font-medium">{request.surname}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">withdrawPoints:</span>
                                <span className="font-medium">{request.withdrawPoints}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Requested At:</span>
                                <span className="font-medium">{formatTimestamp(request.timestamp)}</span>
                              </div>
                              <button
                                onClick={() => openTaskForm(request.id)}
                                className="w-full bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center mt-4"
                               >
                                <FiPlus className="mr-2" />
                                Give Task
                              </button>
                            </div>
                          </motion.div>
                        </div>
                      ))}
                    </Slider>
                  ) : (
                    <div className="text-center py-4">
                      <FaRupeeSign className="mx-auto text-4xl text-gray-400 mb-2" />
                      <p className="text-gray-500">No withdrawal requests found</p>
                    </div>
                  )}
                </div>

                {/* Task Stats Section */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <FiFileText className="mr-2 text-indigo-500" />
                    Task Statistics
                  </h3>
                  
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <div className="bg-white rounded-lg p-3 text-center shadow-sm">
                      <div className="text-2xl font-bold text-indigo-600">{taskStats.total}</div>
                      <div className="text-xs text-gray-500">Total Tasks</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center shadow-sm">
                      <div className="text-2xl font-bold text-amber-600">{taskStats.pending}</div>
                      <div className="text-xs text-gray-500">Pending</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center shadow-sm">
                      <div className="text-2xl font-bold text-green-600">{taskStats.completed}</div>
                      <div className="text-xs text-gray-500">Completed</div>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowDepositsModal(true);
                      fetchDeposits();
                    }}
                    className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center mt-4"
                  >
                    Show Deposits
                  </button>
                </div>

                {/* Task List Section */}
                <div className="lg:col-span-2">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <FiFileText className="mr-2 text-indigo-500" />
                    Assigned Tasks
                  </h3>
                  
                  {tasks.length > 0 ? (
                    <div className="space-y-3">
                      {tasks.map(task => (
                        <motion.div
                          key={task.taskId}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3 }}
                          className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              {/* Task completion checkbox */}
                              <div className="flex flex-col space-y-2 mb-2">
                                {/* Purchase checkbox */}
                                <div className="flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={task.purchase || false}
                                    onChange={(e) => handlePurchaseSaleCheck(task.taskId, 'purchase', e.target.checked)}
                                    className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500 focus:ring-2"
                                  />
                                  <span className="ml-2 text-xs text-gray-600">
                                    {task.purchase ? 'Purchase ✓' : 'Mark Purchase'}
                                  </span>
                                </div>
                                
                                {/* Sale checkbox */}
                                <div className="flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={task.sale || false}
                                    onChange={(e) => handlePurchaseSaleCheck(task.taskId, 'sale', e.target.checked)}
                                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                                  />
                                  <span className="ml-2 text-xs text-gray-600">
                                    {task.sale ? 'Sale ✓' : 'Mark Sale'}
                                  </span>
                                </div>
                              </div>
                              
                              <div>
                                {task.imageUrl && (
                                  <div className="mb-2">
                                    <img src={task.imageUrl} alt="Task" className="h-20 w-20 object-cover rounded border" />
                                  </div>
                                )}
                                <div className="flex items-center justify-between">
                                  <h4 className="font-medium text-gray-800">Task {task.taskId}: {task.title}</h4>
                                  <div className="flex gap-2">
                                    <>
                                      <button
                                        onClick={() => openEditTaskForm(task)}
                                        className="text-indigo-600 hover:text-indigo-800 transition-colors"
                                        aria-label={`Edit Task ${task.taskId}`}
                                      >
                                        <FiEdit2 className="text-lg" />
                                      </button>
                                      {task.checked && (
                                        <button
                                          onClick={() => openGivePointsModal(task.taskId)}
                                          className="text-amber-600 hover:text-amber-800 transition-colors ml-2 border border-amber-300 rounded px-2 py-1 text-xs font-semibold"
                                          aria-label={`Give Points for Task ${task.taskId}`}
                                        >
                                          Give Points
                                        </button>
                                      )}
                                    </>
                                  </div>
                                </div>
                                <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                                {task.withdrawRequestId && (
                                  <p className="text-sm text-gray-600 mt-1">
                                    Related Withdrawal Request ID: {task.withdrawRequestId}
                                  </p>
                                )}
                                <div className="mt-2 text-xs text-gray-500">
                                  <div>
                                    <FiClock className="inline mr-1" />
                                    Assigned: {formatTimestamp(task.assignedAt)}
                                  </div>
                                  {task.checked && task.completedAt && (
                                    <div className="text-green-600">
                                      <FiCheckCircle className="inline mr-1" />
                                      Completed: {formatTimestamp(task.completedAt)}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-col space-y-1">
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                task.status === 'completed'
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-amber-100 text-amber-800'
                              }`}>
                                {task.status.charAt(0).toUpperCase() + task.status.slice(1)}
                              </span>
                              {task.purchase && (
                                <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-800">
                                  Purchase ✓
                                </span>
                              )}
                              {task.sale && (
                                <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-800">
                                  Sale ✓
                                </span>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <FiFileText className="mx-auto text-4xl text-gray-400 mb-2" />
                      <p className="text-gray-500">No tasks assigned yet</p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Gifts Tab */}
          {activeTab === 'gifts' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
              className="p-4 sm:p-6"
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                  <FiGift className="mr-2 text-purple-500" />
                  Assigned Extra Commission
                </h3>
                <button
                  onClick={openGiftForm}
                  className="bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 transition-colors flex items-center"
                >
                  <FiGift className="mr-2" />
                  Assign Extra Commission
                </button>
              </div>
              
              {gifts.length > 0 ? (
                <div className="space-y-3">
                  {gifts.map(gift => (
                    <motion.div
                      key={gift.giftId}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                      className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium text-gray-800">Gift {gift.giftId}: {gift.giftName}</h4>
                            <button
                              onClick={() => openEditGiftForm(gift)}
                              className="text-purple-600 hover:text-purple-800 transition-colors"
                              aria-label={`Edit Gift ${gift.giftId}`}
                            >
                              <FiEdit2 className="text-lg" />
                            </button>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">Points: {gift.points}</p>
                          <div className="mt-2 text-xs text-gray-500">
                            <div>
                              <FiClock className="inline mr-1" />
                              Assigned: {formatTimestamp(gift.assignedAt)}
                            </div>
                            <div>
                              <FiClock className="inline mr-1" />
                              Updated: {formatTimestamp(gift.updatedAt)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FiGift className="mx-auto text-4xl text-gray-400 mb-2" />
                  <p className="text-gray-500">No Extra Commission assigned yet</p>
                </div>
              )}
            </motion.div>
          )}
        </div>
      </main>

      {/* Media Modal */}
      <AnimatePresence>
        {mediaModal.isOpen && (
          <MediaModal
            mediaUrl={mediaModal.mediaUrl}
            mediaType={mediaModal.mediaType}
            onClose={closeMediaModal}
          />
        )}
      </AnimatePresence>

      {/* Task Form Modal */}
      <AnimatePresence>
        {showTaskForm && (
          <TaskFormModal
            taskForm={taskForm}
            setTaskForm={setTaskForm}
            handleTaskSubmit={handleTaskSubmit}
            closeTaskForm={closeTaskForm}
            isEditing={isEditingTask}
            taskPhoto={taskPhoto}
            setTaskPhoto={setTaskPhoto}
          />
        )}
      </AnimatePresence>

      {/* Gift Form Modal */}
      <AnimatePresence>
        {showGiftForm && (
          <GiftFormModal
            giftForm={giftForm}
            setGiftForm={setGiftForm}
            handleGiftSubmit={handleGiftSubmit}
            closeGiftForm={closeGiftForm}
            isEditing={isEditingGift}
          />
        )}
      </AnimatePresence>

      {/* Give Points Modal */}
      <AnimatePresence>
        {showGivePointsModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="relative bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full mx-4"
            >
              <button
                onClick={closeGivePointsModal}
                className="absolute top-4 right-4 text-gray-600 hover:text-gray-900 bg-white/80 rounded-full p-1 z-10"
                aria-label="Close give points form"
              >
                <FiX className="text-xl" />
              </button>
              <h3 className="text-lg font-semibold text-amber-700 mb-4 flex items-center">
                <FaRupeeSign className="mr-2" />
                Give Points for Task {givePointsTaskId}
              </h3>
              <form onSubmit={handleGivePointsSubmit}>
                <div className="mb-4">
                  <label htmlFor="givePointsValue" className="block text-sm font-medium text-gray-700 mb-1">
                    Points (number only)
                  </label>
                  <input
                    type="text"
                    id="givePointsValue"
                    name="givePointsValue"
                    value={givePointsValue}
                    onChange={handleGivePointsChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    placeholder="Enter points to give"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="off"
                    required
                  />
                </div>
                <div className="flex space-x-4">
                  <button
                    type="submit"
                    className="flex-1 bg-amber-600 text-white py-2 px-4 rounded-lg hover:bg-amber-700 transition-colors flex items-center justify-center"
                    disabled={givePointsLoading}
                  >
                    {givePointsLoading ? 'Giving...' : 'Give Points'}
                  </button>
                  <button
                    type="button"
                    onClick={closeGivePointsModal}
                    className="flex-1 bg-gray-300 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors flex items-center justify-center"
                  >
                    <FiX className="mr-2" />
                    Cancel
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Deposits Modal */}
      <AnimatePresence>
        {showDepositsModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="relative bg-white rounded-2xl shadow-2xl p-6 max-w-2xl w-full mx-4"
            >
              <button
                onClick={() => setShowDepositsModal(false)}
                className="absolute top-4 right-4 text-gray-600 hover:text-gray-900 bg-white/80 rounded-full p-1 z-10"
                aria-label="Close deposits modal"
              >
                <FiX className="text-xl" />
              </button>
              <h3 className="text-lg font-bold text-blue-700 mb-4 flex items-center">
                <FiCreditCard className="mr-2" /> Deposit Payments
              </h3>
              {depositsLoading ? (
                <div className="text-center py-8">Loading...</div>
              ) : depositsError ? (
                <div className="text-center text-red-500 py-8">{depositsError}</div>
              ) : deposits.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No deposits found.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full border text-sm">
                    <thead>
                      <tr className="bg-blue-100">
                        <th className="px-2 py-1 border">ID</th>
                        <th className="px-2 py-1 border">Amount</th>
                        <th className="px-2 py-1 border">Method</th>
                        <th className="px-2 py-1 border">Payment Number</th>
                        <th className="px-2 py-1 border">Screenshot</th>
                        <th className="px-2 py-1 border">Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deposits.map(dep => (
                        <tr key={dep.id} className="hover:bg-blue-50">
                          <td className="px-2 py-1 border text-center">{dep.id}</td>
                          <td className="px-2 py-1 border text-center">₹{dep.amount}</td>
                          <td className="px-2 py-1 border text-center capitalize">{dep.method}</td>
                          <td className="px-2 py-1 border text-center">{dep.paymentNumber}</td>
                          <td className="px-2 py-1 border text-center">
                            {dep.screenshotUrl ? (
                              <img
                                src={dep.screenshotUrl}
                                alt="Screenshot"
                                className="h-12 w-12 object-cover rounded border mx-auto cursor-pointer"
                                onClick={() => setDepositImageModal({ open: true, url: dep.screenshotUrl })}
                              />
                            ) : (
                              <span className="text-gray-400">N/A</span>
                            )}
                          </td>
                          <td className="px-2 py-1 border text-center">{formatTimestamp(dep.timestamp)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Deposit Image Modal */}
      <AnimatePresence>
        {depositImageModal.open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="relative bg-white rounded-2xl shadow-2xl p-4 max-w-2xl w-full mx-4 flex flex-col items-center"
            >
              <button
                onClick={() => setDepositImageModal({ open: false, url: null })}
                className="absolute top-4 right-4 text-gray-600 hover:text-gray-900 bg-white/80 rounded-full p-1 z-10"
                aria-label="Close deposit image modal"
              >
                <FiX className="text-2xl" />
              </button>
              <img
                src={depositImageModal.url}
                alt="Deposit Screenshot"
                className="w-full h-auto max-h-[80vh] object-contain rounded"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Invite List Modal */}
      <AnimatePresence>
        {showInviteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="relative bg-white rounded-2xl shadow-2xl p-2 sm:p-6 w-full max-w-xs sm:max-w-lg md:max-w-2xl mx-2 sm:mx-4 max-h-[90vh] overflow-y-auto"
            >
              <button
                onClick={() => setShowInviteModal(false)}
                className="absolute top-4 right-4 text-gray-600 hover:text-gray-900 bg-white/80 rounded-full p-1 z-10"
                aria-label="Close invite list modal"
              >
                <FiX className="text-xl" />
              </button>
              <h3 className="text-lg font-bold text-blue-700 mb-4 flex items-center">
                <FiUsers className="mr-2" /> Invite Lists
              </h3>
              {inviteLoading ? (
                <div className="text-center py-8">Loading...</div>
              ) : inviteBatches.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No invite lists found.</div>
              ) : (
                <>
                  {/* Show points given for this batch */}
                  <div className="mb-2 text-right text-green-700 font-semibold text-xs sm:text-base">
                    Points Given to this Invite List: {inviteBatches[selectedInviteBatchIdx].points || 0}
                  </div>
                  <div className="mb-4 font-semibold text-blue-700 text-sm sm:text-base text-center">
                    {inviteBatches[selectedInviteBatchIdx].batchName.replace('invite', 'Invite List ')}
                  </div>
                  {/* Table with scrollable wrapper and consistent styling */}
                  <div className="invite-table-wrapper" style={{ maxHeight: 250, overflowY: 'auto', width: '100%', overflowX: 'auto' }}>
                    <table className="invite-table" style={{ width: '100%', minWidth: 400, borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#e3eafc' }}>
                          <th style={{ padding: 10, border: '1px solid #ddd' }}>Sr. No</th>
                          <th style={{ padding: 10, border: '1px solid #ddd' }}>Name</th>
                          <th style={{ padding: 10, border: '1px solid #ddd' }}>Mobile</th>
                          <th style={{ padding: 10, border: '1px solid #ddd' }}>Link</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inviteBatches[selectedInviteBatchIdx].invites.map((invite, idx) => (
                          <tr
                            key={invite.key}
                            style={{ background: idx % 2 === 0 ? '#f9f9f9' : '#fff' }}
                          >
                            <td style={{ padding: 10, border: '1px solid #ddd', textAlign: 'center', color: '#888' }}>{idx + 1}</td>
                            <td style={{ padding: 10, border: '1px solid #ddd' }}>{invite.name}</td>
                            <td style={{ padding: 10, border: '1px solid #ddd' }}>{invite.mobile}</td>
                            <td style={{ padding: 10, border: '1px solid #ddd', wordBreak: 'break-all' }}>
                              <a
                                href={invite.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#1976d2', textDecoration: 'underline' }}
                              >
                                {invite.link}
                              </a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* Points Form */}
                  <div className="flex flex-col sm:flex-row items-center gap-2 mt-4 w-full">
                    <input
                      type="number"
                      min="1"
                      value={batchPoints}
                      onChange={e => handleBatchPointsChange(e.target.value)}
                      placeholder="Points for Invite list"
                      className="w-full sm:w-40 px-3 py-2 border rounded focus:ring-2 focus:ring-green-400 text-xs sm:text-base"
                      style={{ fontSize: 15 }}
                      disabled={batchPointsLoading}
                    />
                    <button
                      onClick={handleGiveBatchPoints}
                      disabled={batchPointsLoading}
                      className="w-full sm:w-auto bg-green-600 text-white px-5 py-2 rounded hover:bg-green-700 transition-colors font-semibold shadow"
                    >
                      {batchPointsLoading ? (
                        <span>
                          <svg className="inline w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                          </svg>
                          Giving...
                        </span>
                      ) : (
                        "Give Point"
                      )}
                    </button>
                  </div>
                  {/* Table styles for consistency */}
                  <style>{`
                    .invite-table-wrapper {
                      width: 100%;
                      overflow-x: auto;
                      max-height: 250px;
                      overflow-y: auto;
                    }
                    .invite-table {
                      width: 100%;
                      border-collapse: collapse;
                      min-width: 400px;
                    }
                    .invite-table th, .invite-table td {
                      padding: 10px;
                      border: 1px solid #ddd;
                    }
                    .invite-table th {
                      background: #e3eafc;
                    }
                    .invite-table tr:nth-child(even) {
                      background: #f9f9f9;
                    }
                    .invite-table tr:nth-child(odd) {
                      background: #fff;
                    }
                  `}</style>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style jsx>{`
        .withdrawal-slider :global(.slick-prev),
        .withdrawal-slider :global(.slick-next) {
          background: rgba(79, 70, 229, 0.8);
          border-radius: 50%;
          width: 30px;
          height: 30px;
          z-index: 10;
        }
        .withdrawal-slider :global(.slick-prev) {
          left: 10px;
        }
        .withdrawal-slider :global(.slick-next) {
          right: 10px;
        }
        .withdrawal-slider :global(.slick-prev:before),
        .withdrawal-slider :global(.slick-next:before) {
          color: white;
          font-size: 20px;
        }
        .withdrawal-slider :global(.slick-dots li button:before) {
          color: #4f46e5;
          font-size: 12px;
        }
        .withdrawal-slider :global(.slick-dots li.slick-active button:before) {
          color: #4f46e5;
        }
      `}</style>
    </div>
  );
};

export default UserProfile;